import { REQUIREMENTS, isOfficeAvailable, isSupported, requirementLabel } from './capabilities.js';
import { cancelled, failed, ok, unsupported, type HostResult } from './result.js';

export interface DeckFetchProgress {
  readonly slicesReceived: number;
  readonly sliceCount: number;
  readonly bytesReceived: number;
}

export interface DeckFetchOptions {
  /** SPIKE 1 measured 4 MB slices as the practical size. */
  readonly sliceSize?: number;
  readonly onProgress?: (progress: DeckFetchProgress) => void;
  readonly signal?: AbortSignal;
}

export interface DeckBytes {
  readonly bytes: Uint8Array;
  readonly elapsedMs: number;
  readonly sliceCount: number;
}

const DEFAULT_SLICE_SIZE = 4_194_304;

/**
 * Fetches the current deck as bytes.
 *
 * Requirement: `File 1.1`. Degradation: none is possible — reading the file is
 * how this product works — so the caller shows "SlideForge needs a newer
 * version of PowerPoint" rather than a broken pane.
 *
 * Two things here are not optional:
 *
 * 1. `closeAsync` runs on EVERY path, including errors and cancellation. Office
 *    allows at most two open document copies; a leaked handle makes the next
 *    call fail with an opaque error that looks nothing like its cause.
 * 2. Slices are requested sequentially. Firing all of them at once is faster on
 *    desktop and unreliable on Office on the web, where SPIKE 1 measured 10-40
 *    seconds for a large deck.
 */
export async function getDeckBytes(options: DeckFetchOptions = {}): Promise<HostResult<DeckBytes>> {
  if (!isOfficeAvailable()) {
    return failed('Office.js is not available. This code must run inside an Office add-in.');
  }
  if (!isSupported(REQUIREMENTS.file)) {
    return unsupported(
      requirementLabel(REQUIREMENTS.file),
      'SlideForge cannot read the presentation without it. Update Office, or use SlideForge on the web.',
    );
  }

  const started = now();
  const file = await openFile(options.sliceSize ?? DEFAULT_SLICE_SIZE);
  if (!file.ok) return file;

  try {
    // Office reports the slice count it actually chose, which is not always
    // ceil(size / requested) — it clamps the slice size on some hosts.
    const sliceCount = Math.max(1, file.value.sliceCount);
    const chunks: Uint8Array[] = [];
    let bytesReceived = 0;

    for (let i = 0; i < sliceCount; i += 1) {
      if (options.signal?.aborted) return cancelled('Reading the presentation was cancelled.');
      const slice = await getSlice(file.value, i);
      if (!slice.ok) return slice;
      chunks.push(slice.value);
      bytesReceived += slice.value.byteLength;
      options.onProgress?.({ slicesReceived: i + 1, sliceCount, bytesReceived });
    }

    const bytes = concat(chunks, bytesReceived);
    return ok({ bytes, elapsedMs: Math.round(now() - started), sliceCount });
  } finally {
    await closeFile(file.value);
  }
}

function openFile(sliceSize: number): Promise<HostResult<Office.File>> {
  return new Promise((resolve) => {
    try {
      Office.context.document.getFileAsync(Office.FileType.Compressed, { sliceSize }, (result) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          resolve(failed(result.error?.message ?? 'The presentation could not be read.', String(result.error?.code ?? '')));
          return;
        }
        resolve(ok(result.value));
      });
    } catch (error) {
      resolve(failed(error instanceof Error ? error.message : String(error)));
    }
  });
}

function getSlice(file: Office.File, index: number): Promise<HostResult<Uint8Array>> {
  return new Promise((resolve) => {
    try {
      file.getSliceAsync(index, (result) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          resolve(
            failed(
              `Part ${index + 1} of the presentation could not be read: ${result.error?.message ?? 'unknown error'}`,
              String(result.error?.code ?? ''),
            ),
          );
          return;
        }
        resolve(ok(toUint8Array(result.value.data)));
      });
    } catch (error) {
      resolve(failed(error instanceof Error ? error.message : String(error)));
    }
  });
}

function closeFile(file: Office.File): Promise<void> {
  return new Promise((resolve) => {
    try {
      file.closeAsync(() => resolve());
    } catch {
      resolve();
    }
  });
}

function toUint8Array(data: unknown): Uint8Array {
  if (data instanceof Uint8Array) return data;
  if (Array.isArray(data)) return Uint8Array.from(data as number[]);
  if (data instanceof ArrayBuffer) return new Uint8Array(data);
  return new Uint8Array(0);
}

function concat(chunks: readonly Uint8Array[], totalLength: number): Uint8Array {
  const merged = new Uint8Array(totalLength);
  let offset = 0;
  for (const chunk of chunks) {
    merged.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return merged;
}

function now(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}
